import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useMeta } from '../hooks/useMeta';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';
import { getStay, getReviewsForProperty, CURRENCY_SYMBOLS } from '../api';
import './Listing.css';
import SearchMap from '../components/SearchMap';

export default function Listing() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [stay, setStay]           = useState(null);
  useMeta(
    stay ? `${stay.name} – ${stay.location}` : null,
    stay ? `${stay.name} in ${stay.location}. Halal-certified, alcohol-free stay verified by SunnaStays. From ${stay.price_per_night ? `£${stay.price_per_night}` : ''} per night.` : null
  );
  const [loading, setLoading]     = useState(true);
  const [wishlisted, setWishlisted] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [selectedCheckin, setSelectedCheckin] = useState(null);
  const [selectedCheckout, setSelectedCheckout] = useState(null);
  const [selectedGuests, setSelectedGuests] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [mosque, setMosque] = useState(null);
  const [halalRestaurants, setHalalRestaurants] = useState([]);
  const [halalShops, setHalalShops] = useState([]);
  const [searchParams] = useSearchParams();

  const fetchNearestMosque = useCallback(async function fetchNearestMosque(lat, lng) {
    try {
      // Query nodes, ways, and relations — many mosques are mapped as buildings (ways), not points
      const query = `[out:json][timeout:15];
(
  node[amenity=place_of_worship][religion~"muslim|islam"](around:5000,${lat},${lng});
  way[amenity=place_of_worship][religion~"muslim|islam"](around:5000,${lat},${lng});
  node[amenity=mosque](around:5000,${lat},${lng});
  way[amenity=mosque](around:5000,${lat},${lng});
);
out center 20;`;
      const res = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST', body: query,
      });
      const data = await res.json();
      if (!data.elements?.length) return;
      // Ways have center coords; nodes have lat/lon directly
      const closest = data.elements
        .map(el => {
          const elLat = el.lat ?? el.center?.lat;
          const elLon = el.lon ?? el.center?.lon;
          return { ...el, elLat, elLon, dist: haversine(lat, lng, elLat, elLon) };
        })
        .filter(el => el.elLat != null)
        .sort((a, b) => a.dist - b.dist)[0];
      if (!closest) return;
      const name = closest.tags?.name || closest.tags?.['name:en'] || 'Local mosque';
      setMosque({ name, miles: closest.dist.toFixed(1) });
    } catch (_) {}
  }, []);

  const fetchNearbyHalal = useCallback(function fetchNearbyHalal(lat, lng, attempt = 0) {
    if (!window.google?.maps?.places) {
      if (attempt < 10) setTimeout(() => fetchNearbyHalal(lat, lng, attempt + 1), 800);
      return;
    }
    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    const location = new window.google.maps.LatLng(lat, lng);

    const search = (keyword, maxResults, setter) => {
      service.nearbySearch({
        location,
        rankBy: window.google.maps.places.RankBy.DISTANCE,
        keyword,
      }, (results, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !results?.length) return;
        const mapped = results.slice(0, maxResults).map(r => ({
          name: r.name,
          rating: r.rating || null,
          miles: haversine(lat, lng, r.geometry.location.lat(), r.geometry.location.lng()).toFixed(1),
        }));
        setter(mapped);
      });
    };

    search('halal restaurant', 3, setHalalRestaurants);
    search('halal butcher', 2, setHalalShops);
  }, []);

  useEffect(() => {
    if (searchParams.get('booking') === 'true') {
      setShowBooking(true);
    }
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    getStay(slug)
      .then(data => {
        setStay(data);
        setLoading(false);
        if (data?.id) getReviewsForProperty(data.id).then(r => { setReviews(r); }).catch(() => {});
        if (data?.lat && data?.lng) {
          fetchNearestMosque(data.lat, data.lng);
          fetchNearbyHalal(data.lat, data.lng);
        } else {
          // Geocode via Nominatim — use full address if available, else city/country
          const geocodeQuery = data?.address || data?.location;
          if (geocodeQuery) {
            fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(geocodeQuery)}&format=json&limit=1`)
              .then(r => r.json())
              .then(results => {
                if (results?.[0]) {
                  const lt = parseFloat(results[0].lat);
                  const ln = parseFloat(results[0].lon);
                  fetchNearestMosque(lt, ln);
                  fetchNearbyHalal(lt, ln);
                }
              })
              .catch(() => {});
          }
        }
      })
      .catch(() => setLoading(false));
  }, [slug, fetchNearestMosque, fetchNearbyHalal]);

  function haversine(lat1, lon1, lat2, lon2) {
    const R = 3958.8;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2
      + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  if (loading) return (
    <div className="listing-page">
      <div className="listing-loading">
        <div className="listing-hero-skeleton" />
        <div className="container listing-content-skeleton">
          <div className="skel-line skel-title" />
          <div className="skel-line skel-sub" />
          <div className="skel-line" />
        </div>
      </div>
    </div>
  );

  if (!stay) return (
    <div className="listing-page listing-not-found">
      <h2>Stay not found</h2>
      <button className="btn-primary" onClick={() => navigate('/search')}>Browse all stays</button>
    </div>
  );

  const nights = selectedCheckin && selectedCheckout
    ? Math.round((selectedCheckout - selectedCheckin) / (1000 * 60 * 60 * 24))
    : 0;
  const serviceFee = nights > 0 ? Math.round(stay.price * nights * 0.10) : 0;
  const total = nights > 0 ? stay.price * nights + serviceFee : 0;
  const currencySymbol = CURRENCY_SYMBOLS[stay.currency] || stay.currency || '£';

  return (
    <div className="listing-page">
      {/* PHOTO HERO */}
      <div className="listing-hero">
        {stay.photos && stay.photos.length > 0 ? (
          <>
            <div className="listing-img-main" onClick={() => setLightbox({ photos: stay.photos, index: 0 })}>
              <img src={stay.photos[0]} alt={stay.name} />
            </div>
            <div className="listing-img-grid">
              {[stay.photos[1] || stay.photos[0], stay.photos[2] || stay.photos[0]].map((url, i) => (
                <div key={i} className="listing-img-sub" onClick={() => setLightbox({ photos: stay.photos, index: i + 1 < stay.photos.length ? i + 1 : 0 })}>
                  <img src={url} alt={stay.name} />
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="listing-img-main" style={{ background: stay.gradient || 'linear-gradient(135deg,var(--terra-muted),var(--terra))' }}>
              <span className="listing-img-emoji">{stay.emoji || '🏠'}</span>
            </div>
            <div className="listing-img-grid">
              {['🌅', '🛏️'].map((img, i) => (
                <div key={i} className="listing-img-sub" style={{ background: i === 0 ? 'linear-gradient(135deg,var(--sand-deep),#c0a070)' : 'linear-gradient(135deg,#a8c4b8,#4a7c59)' }}>
                  <span>{img}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* CONTENT */}
      <div className="container listing-content">
        <div className="listing-left">
          <h1 className="listing-title">{stay.name}</h1>
          <div className="listing-meta-row">
            {reviews.length > 0 ? (
              <>
                <span>★ <strong>{(reviews.reduce((sum,r) => sum+r.rating,0)/reviews.length).toFixed(1)}</strong> · {reviews.length} review{reviews.length !== 1 ? 's' : ''}</span>
                <span className="listing-dot">·</span>
              </>
            ) : (
              <>
                <span className="listing-no-reviews">No reviews yet</span>
                <span className="listing-dot">·</span>
              </>
            )}
            <span>📍 {stay.location}</span>
            <span className="halal-tick listing-halal">✓ Halal certified</span>
          </div>

          <hr className="listing-divider" />

          <div className="host-row">
            <div className="host-avatar">{stay.host?.emoji || '👤'}</div>
            <div>
              <div className="host-name">Hosted by {stay.host?.name}</div>
              <div className="host-meta">
                {stay.host?.isSuperhost && 'Superhost · '}
                Joined {stay.host?.since} · {stay.host?.languages?.join(', ')}
              </div>
            </div>
          </div>

          <hr className="listing-divider" />

          <p className="listing-desc">{stay.description}</p>

          <hr className="listing-divider" />

          <div className="halal-standards">
            <h4>🟢 SunnaStays Halal Standards</h4>
            <ul className="halal-list">
              {stay.halalStandards?.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>

          <hr className="listing-divider" />

          {(() => {
            const RULE_LABELS = {
              noSmoking:   { icon: '🚭', label: 'No smoking' },
              noParties:   { icon: '🎉', label: 'No parties or events' },
              noPets:      { icon: '🐾', label: 'No pets' },
              quietHours:  { icon: '🌙', label: 'Quiet hours after 10 pm' },
              noUnmahrems: { icon: '🤝', label: 'No unrelated mixed-gender gatherings' },
              shoesOff:    { icon: '👟', label: 'Shoes off indoors' },
            };
            const active = Object.entries(stay.houseRules || {})
              .filter(([k, v]) => k !== 'custom' && v === true);
            const custom = stay.houseRules?.custom;
            if (!active.length && !custom) return null;
            return (
              <>
                <div className="house-rules-section">
                  <h4 className="house-rules-title">🏠 House rules</h4>
                  <div className="house-rules-list">
                    {active.map(([key]) => (
                      <div key={key} className="house-rule-item">
                        <span className="house-rule-icon">{RULE_LABELS[key]?.icon}</span>
                        <span>{RULE_LABELS[key]?.label || key}</span>
                      </div>
                    ))}
                    {custom && (
                      <div className="house-rule-item house-rule-custom">
                        <span className="house-rule-icon">📋</span>
                        <span>{custom}</span>
                      </div>
                    )}
                  </div>
                </div>
                <hr className="listing-divider" />
              </>
            );
          })()}

          {stay.amenities?.length > 0 && (
            <>
              <div className="amenities-title">What this place offers</div>
              <div className="amenity-grid">
                {stay.amenities.map(a => (
                  <div key={a.label} className="amenity">
                    <span className="amenity-icon">{a.icon}</span> {a.label}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* BOOKING CARD */}
        <div className="listing-right">
          <div className="booking-card">
            <div className="booking-price">{currencySymbol}{stay.price} <span>per night</span></div>
            {reviews.length > 0 && (
              <div className="booking-rating">
                <span style={{ color: 'var(--gold)' }}>★</span> {(reviews.reduce((sum,r) => sum+r.rating,0)/reviews.length).toFixed(1)} · <span>{reviews.length} review{reviews.length !== 1 ? 's' : ''}</span>
              </div>
            )}

            <div className="halal-cert-badge">🟢 SunnaStays Halal Guarantee included</div>

            <div className="date-inputs" onClick={async e => {
                e.preventDefault();
                e.stopPropagation();
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) { navigate('/signin?redirect=' + encodeURIComponent('/stays/' + stay.slug + '?booking=true')); return; }
                if (!user.email_confirmed_at) {
                  alert('Please verify your email address before booking. Check your inbox for a verification link.');
                  return;
                }
                setShowBooking(true);
              }} style={{cursor:'pointer'}}>
              <div className="date-row">
                <div className="date-cell">
                  <div className="date-label">Check-in</div>
                  <div className={`date-value ${selectedCheckin ? '' : 'date-value--empty'}`}>
                    {selectedCheckin ? selectedCheckin.toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'}) : 'Add date'}
                  </div>
                </div>
                <div className="date-cell">
                  <div className="date-label">Check-out</div>
                  <div className={`date-value ${selectedCheckout ? '' : 'date-value--empty'}`}>
                    {selectedCheckout ? selectedCheckout.toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'}) : 'Add date'}
                  </div>
                </div>
              </div>
              <div className="guests-cell">
                <div className="date-label">Guests</div>
                <div className={`date-value ${selectedGuests >= 1 ? '' : 'date-value--empty'}`}>
                  {selectedGuests >= 1 ? `${selectedGuests} guest${selectedGuests !== 1 ? 's' : ''}` : 'Add guests'}
                </div>
              </div>
            </div>

            {nights > 0 && (
            <div className="booking-total">
              <div className="booking-line">
                <span>{currencySymbol}{stay.price} × {nights} night{nights !== 1 ? 's' : ''}</span>
                <span>{currencySymbol}{stay.price * nights}</span>
              </div>
              <div className="booking-line">
                <span>SunnaStays service fee (10%)</span>
                <span>{currencySymbol}{serviceFee}</span>
              </div>
              <div className="booking-line booking-line--total">
                <span>Total ({stay.currency || 'GBP'})</span>
                <span>{currencySymbol}{total}</span>
              </div>
              <div className="booking-currency-note">Charged in {stay.currency || 'GBP'}</div>
            </div>
            )}

            {nights > 0 && selectedCheckin && (() => {
              const policy = stay.cancellationPolicy;
              const checkin = new Date(selectedCheckin);
              const now = new Date();

              if (policy === 'flexible') {
                const deadline = new Date(checkin);
                deadline.setDate(deadline.getDate() - 1);
                if (deadline > now) return (
                  <div className="booking-cancellation-note">
                    Free cancellation before <strong>{deadline.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}</strong>
                  </div>
                );
              }
              if (policy === 'moderate') {
                const deadline = new Date(checkin);
                deadline.setDate(deadline.getDate() - 5);
                if (deadline > now) return (
                  <div className="booking-cancellation-note">
                    Free cancellation before <strong>{deadline.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}</strong>
                  </div>
                );
              }
              if (policy === 'firm') {
                const fullDeadline = new Date(checkin);
                fullDeadline.setDate(fullDeadline.getDate() - 30);
                const halfDeadline = new Date(checkin);
                halfDeadline.setDate(halfDeadline.getDate() - 7);
                if (fullDeadline > now) return (
                  <div className="booking-cancellation-note">
                    Free cancellation before <strong>{fullDeadline.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}</strong>
                  </div>
                );
                if (halfDeadline > now) return (
                  <div className="booking-cancellation-note">
                    50% refund if cancelled before <strong>{halfDeadline.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}</strong>
                  </div>
                );
              }
              return (
                <div className="booking-cancellation-note">
                  This booking is non-refundable
                </div>
              );
            })()}

            <button
              className="btn-primary booking-btn"
              onClick={async e => {
                e.preventDefault();
                e.stopPropagation();
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) {
                  navigate('/signin?redirect=' + encodeURIComponent('/stays/' + stay.slug + '?booking=true'));
                  return;
                }
                if (!user.email_confirmed_at) {
                  alert('Please verify your email address before booking. Check your inbox for a verification link.');
                  return;
                }
                setShowBooking(true);
              }}
            >
              Reserve
            </button>
            <p className="booking-note">You won't be charged yet</p>
          </div>

          <button
            className="wishlist-btn"
            onClick={() => setWishlisted(w => !w)}
          >
            {wishlisted ? '❤️ Saved' : '🤍 Save to wishlist'}
          </button>
        </div>
      </div>

      {((stay.lat && stay.lng) || mosque) && (
        <div className="listing-map-section">
          <div className="container">
            <hr className="listing-divider" />
            <h3 className="listing-section-title">Where you'll be</h3>
            <div className="listing-nearby-row">
              <div className="listing-nearby-group">
                <div className="listing-nearby-label">📍 Location</div>
                <div className="listing-nearby-line">{stay.location}</div>
                {mosque && (
                  <div className="listing-nearby-line">🕌 <strong>{mosque.name}</strong> — {mosque.miles} miles away</div>
                )}
              </div>
              {halalRestaurants.length > 0 && (
                <div className="listing-nearby-group">
                  <div className="listing-nearby-label">🍽️ Halal restaurants nearby</div>
                  {halalRestaurants.map((r, i) => (
                    <div key={i} className="listing-nearby-line">
                      <strong>{r.name}</strong>{r.rating ? ` · ★ ${r.rating}` : ''} — {r.miles} miles away
                    </div>
                  ))}
                </div>
              )}
              {halalShops.length > 0 && (
                <div className="listing-nearby-group">
                  <div className="listing-nearby-label">🥩 Halal food shops nearby</div>
                  {halalShops.map((r, i) => (
                    <div key={i} className="listing-nearby-line">
                      <strong>{r.name}</strong> — {r.miles} miles away
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {stay.lat && stay.lng && (
            <div className="listing-map-wrap">
              <SearchMap stays={[stay]} onHover={() => {}} hoveredId={null} />
            </div>
          )}
        </div>
      )}

      {reviews.length > 0 && (
        <div className="container listing-reviews">
          <hr className="listing-divider" />
          <h3 className="reviews-title">
            ★ {(reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)} · {reviews.length} review{reviews.length > 1 ? 's' : ''}
          </h3>
          <div className="reviews-grid">
            {reviews.map(r => (
              <div key={r.id} className="review-card-item">
                <div className="review-card-header">
                  <div className="review-avatar">{r.guest_name?.[0] || '?'}</div>
                  <div>
                    <div className="review-guest-name">{r.guest_name}</div>
                    <div className="review-stars-small">
                      {[1,2,3,4,5].map(s => (
                        <span key={s} style={{color: s <= r.rating ? 'var(--gold)' : 'var(--sand-deep)'}}>★</span>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="review-comment">{r.comment}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      <Footer />

      {lightbox && (
        <div className="listing-lightbox" onClick={() => setLightbox(null)}>
          <button className="listing-lightbox-close" onClick={() => setLightbox(null)}>✕</button>
          {lightbox.index > 0 && (
            <button className="listing-lightbox-prev" onClick={e => { e.stopPropagation(); setLightbox(l => ({ ...l, index: l.index - 1 })); }}>‹</button>
          )}
          <img src={lightbox.photos[lightbox.index]} alt="" className="listing-lightbox-img" onClick={e => e.stopPropagation()} />
          {lightbox.index < lightbox.photos.length - 1 && (
            <button className="listing-lightbox-next" onClick={e => { e.stopPropagation(); setLightbox(l => ({ ...l, index: l.index + 1 })); }}>›</button>
          )}
          <div className="listing-lightbox-counter">{lightbox.index + 1} / {lightbox.photos.length}</div>
        </div>
      )}

      {showBooking && (
        <BookingModal
          stay={stay}
          initialCheckin={selectedCheckin}
          initialCheckout={selectedCheckout}
          initialGuests={selectedGuests}
          onClose={(checkin, checkout, guests) => {
            if (checkin) setSelectedCheckin(checkin);
            if (checkout) setSelectedCheckout(checkout);
            if (guests) setSelectedGuests(guests);
            setShowBooking(false);
          }}
        />
      )}
    </div>
  );
}
